import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

const ModalitesPaiementPage = () => {
  useEffect(() => {
    document.title = "Modalités de paiement | Cocoonut Digital Strasbourg";
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Modalités de paiement | Cocoonut Digital Strasbourg</title>
        <meta name="description" content="Modalités de paiement de Cocoonut Digital : abonnements mensuels, prestations ponctuelles, prélèvement SEPA et virement bancaire." />
        <link rel="canonical" href={`${window.location.origin}/modalites-de-paiement`} />
      </Helmet>

      {/* Header */}
      <div className="bg-[#1a3a52] py-8">
        <div className="container mx-auto px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors mb-4">
            <ArrowLeft size={20} />
            <span>Retour à l'accueil</span>
          </Link>
          <h1 className="text-3xl md:text-4xl font-brand-display text-white">Modalités de paiement</h1>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto"
        >
          <div className="space-y-14">

            {/* Intro */}
            <div className="space-y-4">
              <p className="text-lg text-[#151515]/70 leading-relaxed">
                Chez Cocoonut Digital, nous nous engageons à offrir un cadre clair et transparent pour chaque collaboration. Voici les modalités de paiement applicables à nos services.
              </p>
            </div>

            {/* Section 1 — Abonnement mensuel */}
            <div className="space-y-5">
              <h2 className="text-xl md:text-2xl font-semibold text-[#1a3a52]">Abonnement mensuel</h2>
              <p className="text-[#151515]/70 leading-relaxed">
                Certains de nos services, tels que la gestion des réseaux sociaux (social media management), sont proposés sous forme d'abonnement mensuel. Ce format permet un accompagnement régulier et structuré, adapté à vos objectifs.
              </p>
              <div className="bg-[#f5f5f0] rounded-xl p-6 space-y-3">
                <p className="text-sm font-semibold text-[#1a3a52] uppercase tracking-wider font-mono">Modes de paiement acceptés</p>
                <ul className="space-y-2 text-[#151515]/70">
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#d4a574] mt-2 shrink-0"></span>
                    <span><strong className="text-[#151515]">Prélèvement SEPA</strong> — paiement automatique mensuel, sécurisé et sans intervention de votre part. La mise en place requiert votre consentement via un mandat de prélèvement SEPA signé.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#d4a574] mt-2 shrink-0"></span>
                    <span><strong className="text-[#151515]">Virement bancaire</strong> — paiement mensuel sur présentation de facture.</span>
                  </li>
                </ul>
              </div>
              <p className="text-[#151515]/70 leading-relaxed">
                Le tarif, la fréquence de paiement et les conditions de l'abonnement sont définis ensemble en amont et formalisés dans un contrat avant le début de la collaboration.
              </p>
            </div>

            {/* Section 2 — Prestations ponctuelles */}
            <div className="space-y-5">
              <h2 className="text-xl md:text-2xl font-semibold text-[#1a3a52]">Prestations ponctuelles</h2>
              <p className="text-[#151515]/70 leading-relaxed">
                Les prestations réalisées en dehors d'un abonnement sont facturées sur devis. Cela inclut notamment :
              </p>
              <ul className="space-y-2 text-[#151515]/70 pl-2">
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#d4a574] mt-2 shrink-0"></span>
                  <span>Création de contenu photo et vidéo</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#d4a574] mt-2 shrink-0"></span>
                  <span>Shootings photo</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#d4a574] mt-2 shrink-0"></span>
                  <span>Audits de présence digitale</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#d4a574] mt-2 shrink-0"></span>
                  <span>Consulting et accompagnement stratégique</span>
                </li>
              </ul>
              <div className="bg-[#f5f5f0] rounded-xl p-6 space-y-3">
                <p className="text-sm font-semibold text-[#1a3a52] uppercase tracking-wider font-mono">Conditions de règlement</p>
                <ul className="space-y-2 text-[#151515]/70">
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#d4a574] mt-2 shrink-0"></span>
                    <span>Règlement intégral avant la réalisation de la prestation</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#d4a574] mt-2 shrink-0"></span>
                    <span>Ou versement d'un acompte à la commande, suivi du solde à la livraison — selon le projet</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Section 3 — Conditions */}
            <div className="space-y-5">
              <h2 className="text-xl md:text-2xl font-semibold text-[#1a3a52]">Conditions générales</h2>
              <p className="text-[#151515]/70 leading-relaxed">
                Toute collaboration fait l'objet d'un accord préalable entre Cocoonut Digital et le client. Les modalités de paiement sont précisées dans le devis ou le contrat correspondant.
              </p>
              <ul className="space-y-2 text-[#151515]/70 pl-2">
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#d4a574] mt-2 shrink-0"></span>
                  <span>Les termes de chaque engagement sont convenus en amont</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#d4a574] mt-2 shrink-0"></span>
                  <span>Les conditions de paiement sont clairement mentionnées sur chaque document contractuel</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#d4a574] mt-2 shrink-0"></span>
                  <span>La transparence est au coeur de notre démarche</span>
                </li>
              </ul>
              <p className="text-[#151515]/70 leading-relaxed">
                Pour toute question relative aux modalités de paiement, n'hésitez pas à nous contacter directement.
              </p>
            </div>

            {/* Contact CTA */}
            <div className="border-t border-[#151515]/10 pt-8">
              <div className="flex flex-wrap gap-4">
                <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 bg-[#1a3a52] text-white rounded-full text-sm font-medium hover:bg-[#1a3a52]/90 transition-colors" data-testid="payment-terms-contact-btn">
                  Nous contacter
                </Link>
                <Link to="/conditions-generales" className="inline-flex items-center gap-2 px-6 py-3 bg-[#f5f5f0] text-[#151515] rounded-full text-sm font-medium hover:bg-[#151515]/10 transition-colors" data-testid="payment-terms-cgv-btn">
                  Conditions générales de vente
                </Link>
              </div>
            </div>

          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ModalitesPaiementPage;
